import React from 'react';
import { useCanvasStore } from '@/stores/canvasStore';
import { ColorSwatches } from './ColorSwatches';
import { useImagePalette, usePaletteSettings } from './hooks';

const GAP = 12;

/** Swallow pointer events so the canvas does not deselect or start a pan. */
const stop = (e: React.SyntheticEvent) => e.stopPropagation();

/**
 * The selected image's colors as circles centred under it. Lives in the canvas
 * transform layer and counter-scales, so it follows the card at any zoom but
 * stays the same size on screen. Only while the extension is on.
 */
export function PaletteStrip({ zoom }: { zoom: number }) {
  const { enabled, colors, format } = usePaletteSettings();
  const item = useCanvasStore((s) => {
    if (s.selectedItemIds.size !== 1) return undefined;
    const [id] = s.selectedItemIds;
    const found = s.items.find((i) => i.id === id);
    return found?.item_type === 'image' ? found : undefined;
  });
  const url = (item?.content as { url?: string } | undefined)?.url;
  const swatches = useImagePalette(url, enabled && !!item);

  if (!enabled || !item || !swatches) return null;

  return (
    <div
      className="absolute pointer-events-auto z-[60]"
      style={{
        left: item.position_x + item.width / 2,
        top: item.position_y + item.height + GAP / zoom,
        transform: `translateX(-50%) scale(${1 / zoom})`,
        transformOrigin: 'top center',
      }}
      onMouseDown={stop}
      onPointerDown={stop}
      onTouchStart={stop}
      onClick={stop}
      onDoubleClick={stop}
      data-export-ignore="true"
      data-palette-strip="true"
    >
      <div className="rounded-full border border-border/60 bg-popover/95 backdrop-blur shadow-md px-2 py-1.5 animate-in fade-in zoom-in-95 duration-150">
        <ColorSwatches swatches={swatches.slice(0, colors)} format={format} />
      </div>
    </div>
  );
}
